// src/api/features/leads/mutations.ts
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createLead,
  updateLead,
  deleteLead,
  transferLeadProduct,
  createLeadProductMilestone,
  updateLeadProductMilestone,
  deleteLeadProductMilestone,
  upsertLeadProductVisaDetails,
} from "./api";

import type {
  LeadResult,
  LeadWithProducts,
  LeadProductWithMeta,
  PaginatedLeads,
  CreateLeadBody,
  UpdateLeadBody,
  TransferLeadProductBody,
  LeadProductMilestone,
  CreateLeadProductMilestoneBody,
  UpdateLeadProductMilestoneBody,
  LeadProductVisaDetail,
  UpsertLeadProductVisaDetailBody,
} from "./types";

import { LeadsKeys } from "./queries";

/* ============================================
 * Local helper types (cache snapshots)
 * ============================================ */

type LeadsListSnapshot = {
  key: readonly unknown[];
  data: PaginatedLeads | undefined;
};

type UpdateLeadContext = {
  prevDetail?: LeadWithProducts;
  prevLists?: LeadsListSnapshot[];
};

type DeleteLeadContext = {
  prevLists?: LeadsListSnapshot[];
};

/* ============================================
 * CREATE LEAD
 * ============================================ */

export function useCreateLeadMutation() {
  const qc = useQueryClient();

  return useMutation<LeadWithProducts, unknown, CreateLeadBody>({
    mutationFn: (payload) => createLead(payload),

    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LeadsKeys.all });
    },
  });
}

/* ============================================
 * UPDATE LEAD (deep full-sync)
 * ============================================ */

export function useUpdateLeadMutation() {
  const qc = useQueryClient();

  return useMutation<
    LeadWithProducts,
    unknown,
    { id: number | string; data: UpdateLeadBody },
    UpdateLeadContext
  >({
    mutationFn: ({ id, data }) => updateLead(id, data),

    async onMutate({ id, data }) {
      // Cancel active queries
      await Promise.all([
        qc.cancelQueries({ queryKey: LeadsKeys.detail(id) }),
        qc.cancelQueries({ queryKey: LeadsKeys.all }),
      ]);

      const prevDetail = qc.getQueryData(
        LeadsKeys.detail(id)
      ) as LeadWithProducts | undefined;

      const prevLists: LeadsListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: LeadsKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedLeads | undefined,
        }));

      // Optimistic update: detail (deep)
      if (prevDetail) {
        qc.setQueryData(
          LeadsKeys.detail(id),
          {
            ...prevDetail,
            ...data,
          } as LeadWithProducts
        );
      }

      // Optimistic update: basic fields in list rows
      for (const snap of prevLists) {
        const list = snap.data;
        if (!list?.items) continue;

        const patched = list.items.map((l) =>
          l.id === Number(id)
            ? ({ ...l, ...data } as LeadResult)
            : l
        );

        qc.setQueryData<PaginatedLeads>(snap.key, {
          ...list,
          items: patched,
        });
      }

      return { prevDetail, prevLists };
    },

    onError(_err, _vars, ctx) {
      if (!ctx) return;

      // Rollback detail
      if (ctx.prevDetail) {
        qc.setQueryData(LeadsKeys.detail(ctx.prevDetail.id), ctx.prevDetail);
      }

      // Rollback lists
      for (const snap of ctx.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSuccess(updated, { id }) {
      const prevDetail = qc.getQueryData(
        LeadsKeys.detail(id)
      ) as LeadWithProducts | undefined;

      // Merge updated deep model
      if (prevDetail) {
        qc.setQueryData(
          LeadsKeys.detail(id),
          {
            ...prevDetail,
            ...updated,
          } as LeadWithProducts
        );
      } else {
        qc.invalidateQueries({ queryKey: LeadsKeys.detail(id) });
      }
    },

    onSettled(_res, _err, { id }) {
      qc.invalidateQueries({ queryKey: LeadsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: LeadsKeys.all });
    },
  });
}

/* ============================================
 * DELETE LEAD (soft delete)
 * ============================================ */

export function useDeleteLeadMutation() {
  const qc = useQueryClient();

  return useMutation<
    { message: string },
    unknown,
    number | string,
    DeleteLeadContext
  >({
    mutationFn: (id) => deleteLead(id),

    async onMutate(id) {
      await qc.cancelQueries({ queryKey: LeadsKeys.all });

      const numericId = Number(id);

      const prevLists: LeadsListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: LeadsKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedLeads | undefined,
        }));

      // Optimistically remove from all lists
      for (const snap of prevLists) {
        const data = snap.data;
        if (!data?.items) continue;

        qc.setQueryData<PaginatedLeads>(snap.key, {
          ...data,
          items: data.items.filter((l) => l.id !== numericId),
          total: Math.max(0, (data.total ?? 1) - 1),
        });
      }

      // Drop detail cache
      qc.removeQueries({ queryKey: LeadsKeys.detail(id) });

      return { prevLists };
    },

    onError(_err, _id, ctx) {
      for (const snap of ctx?.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSettled() {
      qc.invalidateQueries({ queryKey: LeadsKeys.all });
    },
  });
}

/* ============================================
 * TRANSFER (single product → another user)
 * ============================================ */

/**
 * NOTE:
 * - Backend returns `LeadProductWithMeta` (single row, with product + assignee + transfers).
 * - We don't try to patch the lead detail manually; we just invalidate the lead detail + list
 *   so the next screen load refetches the fresh `LeadWithProducts`.
 */
export function useTransferLeadProductMutation(leadId: number | string) {
  const qc = useQueryClient();

  return useMutation<
    LeadProductWithMeta,
    unknown,
    { leadProductId: number; data: TransferLeadProductBody }
  >({
    mutationFn: ({ leadProductId, data }) =>
      transferLeadProduct(leadProductId, data),

    onSuccess() {
      qc.invalidateQueries({ queryKey: LeadsKeys.detail(leadId) });
      qc.invalidateQueries({ queryKey: LeadsKeys.all });
    },
  });
}

export function useCreateLeadProductMilestoneMutation(leadId: number | string) {
  const qc = useQueryClient();

  return useMutation<
    LeadProductMilestone,
    unknown,
    { leadProductId: number; data: CreateLeadProductMilestoneBody }
  >({
    mutationFn: ({ leadProductId, data }) =>
      createLeadProductMilestone(leadProductId, data),
    onSuccess() {
      qc.invalidateQueries({ queryKey: LeadsKeys.detail(leadId) });
      qc.invalidateQueries({ queryKey: LeadsKeys.all });
    },
  });
}

export function useUpdateLeadProductMilestoneMutation(leadId: number | string) {
  const qc = useQueryClient();

  return useMutation<
    LeadProductMilestone,
    unknown,
    {
      leadProductId: number;
      milestoneId: number;
      data: UpdateLeadProductMilestoneBody;
    }
  >({
    mutationFn: ({ leadProductId, milestoneId, data }) =>
      updateLeadProductMilestone(leadProductId, milestoneId, data),
    onSuccess() {
      qc.invalidateQueries({ queryKey: LeadsKeys.detail(leadId) });
      qc.invalidateQueries({ queryKey: LeadsKeys.all });
    },
  });
}

export function useDeleteLeadProductMilestoneMutation(leadId: number | string) {
  const qc = useQueryClient();

  return useMutation<
    { message?: string },
    unknown,
    { leadProductId: number; milestoneId: number }
  >({
    mutationFn: ({ leadProductId, milestoneId }) =>
      deleteLeadProductMilestone(leadProductId, milestoneId),
    onSuccess() {
      qc.invalidateQueries({ queryKey: LeadsKeys.detail(leadId) });
      qc.invalidateQueries({ queryKey: LeadsKeys.all });
    },
  });
}

export function useUpsertLeadProductVisaDetailsMutation(leadId: number | string) {
  const qc = useQueryClient();

  return useMutation<
    LeadProductVisaDetail,
    unknown,
    { leadProductId: number; data: UpsertLeadProductVisaDetailBody }
  >({
    mutationFn: ({ leadProductId, data }) =>
      upsertLeadProductVisaDetails(leadProductId, data),
    onSuccess() {
      qc.invalidateQueries({ queryKey: LeadsKeys.detail(leadId) });
      qc.invalidateQueries({ queryKey: LeadsKeys.all });
    },
  });
}
